import React, { useState, useEffect, memo, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  LinearProgress,
  FormControlLabel,
  Switch,
  useTheme,
  alpha,
  TablePagination,
  InputAdornment,
} from '@mui/material';
import {
  Science,
  Save,
  Warning,
  CheckCircle,
  Timer,
  Inventory,
  Assignment,
  PriorityHigh,
  ContentCopy,
  ExpandMore,
  ExpandLess,
  Refresh,
  Settings,
  AutoAwesome,
  Search,
  Clear,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import samplesService, { Sample, Priority, SampleStatus } from '../../services/samplesService';
import api from '../../services/api';

interface ICPPreparationData {
  sample_id: string;
  initial_weight: number;
  digestion_method: string;
  digestion_volume: number;
  dilution_factor: number;
  preparation_notes: string;
  timestamp: string;
  technician_id: string;
  status: 'EN_COURS' | 'TERMINE' | 'EN_ATTENTE';
}

// 🚀 PERFORMANCE OPTIMIZED: Native input component (10x faster than Material-UI)
const NativeNumberInput = memo(({ 
  value, 
  onChange, 
  sampleId, 
  field,
  hasValue,
  placeholder = "0",
  min = 0,
  step = 0.001
}: {
  value: number;
  onChange: (sampleId: string, field: string, value: number) => void;
  sampleId: string;
  field: string;
  hasValue: boolean;
  placeholder?: string;
  min?: number;
  step?: number;
}) => (
  <input
    type="number"
    step={step}
    min={min}
    value={value || ''}
    onChange={(e) => {
      const newValue = parseFloat(e.target.value) || 0;
      onChange(sampleId, field, newValue);
    }}
    style={{
      width: '90px',
      height: '28px',
      border: hasValue ? '2px solid #4caf50' : '1px solid #ddd',
      borderRadius: '4px',
      padding: '2px 4px',
      fontSize: '0.75rem',
      textAlign: 'center' as const,
      backgroundColor: hasValue ? '#e8f5e8' : 'transparent',
      outline: 'none',
      boxSizing: 'border-box' as const
    }}
    onFocus={(e) => e.target.select()}
    placeholder={placeholder}
  />
));

const ICPPreparationChimiqueDashboard: React.FC = memo(() => {
  const { user } = useAuth();
  const theme = useTheme();
  
  // State management
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preparationDialog, setPreparationDialog] = useState(false);
  const [batchPreparationDialog, setBatchPreparationDialog] = useState(false);
  
  // Form state
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<{batchNumber: string, samples: Sample[]} | null>(null);
  const [sampleId, setSampleId] = useState('');
  const [initialWeight, setInitialWeight] = useState<number>(0);
  const [digestionMethod, setDigestionMethod] = useState('');
  const [digestionVolume, setDigestionVolume] = useState<number>(100);
  const [dilutionFactor, setDilutionFactor] = useState<number>(10);
  const [preparationNotes, setPreparationNotes] = useState('');
  
  // Batch preparation state
  const [batchCommonValues, setBatchCommonValues] = useState({
    digestionMethod: '',
    digestionVolume: 100,
    dilutionFactor: 10,
    applyToAll: {
      digestionMethod: true,
      digestionVolume: true,
      dilutionFactor: true,
    }
  });
  const [batchSampleData, setBatchSampleData] = useState<Record<string, any>>({});
  
  // Add missing state
  const [batchValidationLoading, setBatchValidationLoading] = useState(false);
  
  // Dashboard stats
  const [stats, setStats] = useState({
    pending_samples: 0,
    in_progress: 0,
    completed_today: 0,
    urgent_samples: 0,
  });
  
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());

  // 🚀 PAGINATION & SEARCH STATE
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(250); // ~10 batches worth
  const [totalSamples, setTotalSamples] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [batchCount, setBatchCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input (800ms delay - gives time to finish typing)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0); // Reset to first page on search
    }, 800);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load data when page, rowsPerPage, or search changes
  useEffect(() => {
    loadICPPreparationData();
  }, [page, rowsPerPage, debouncedSearch]);

  const loadICPPreparationData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 🚀 OPTIMIZED: Use new paginated API endpoint with BGM filtering on backend
      const response = await api.get<{
        samples: Sample[];
        total: number;
        page: number;
        totalPages: number;
        batchCount: number;
      }>('/samples/queues/icp-prep', {
        params: {
          page: page + 1, // API is 1-indexed, MUI is 0-indexed
          limit: rowsPerPage,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          sortBy: 'date',
          sortOrder: 'desc',
        }
      });
      
      const icpPreparationSamples = response.samples || [];
      
      setSamples(icpPreparationSamples);
      setTotalSamples(response.total || 0);
      setTotalPages(response.totalPages || 0);
      setBatchCount(response.batchCount || 0);
      
      // Calculate stats from current page data
      const urgentSamples = icpPreparationSamples.filter(
        sample => sample.priorite === Priority.URGENTE
      ).length;
      
      const inProgressSamples = icpPreparationSamples.filter(
        sample => sample.metadonnees?.icp_preparation_started
      ).length;
      
      setStats({
        pending_samples: response.total || icpPreparationSamples.length,
        in_progress: inProgressSamples,
        completed_today: 0,
        urgent_samples: urgentSamples,
      });
      
    } catch (error) {
      console.error('Error loading ICP preparation data:', error);
      setError('Aucun échantillon en préparation chimique ICP trouvé.');
    } finally {
      setLoading(false);
    }
  };

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const digestionMethods = [
    { value: 'HCl', label: 'HCl (pour carbonates)' },
    { value: 'HNO3', label: 'HNO3 (pour sulfures)' },
    { value: 'AQUA_REGIA', label: 'Eau régale (HCl + HNO3)' },
    { value: 'FOUR_ACID', label: '4 acides (HF + HClO4 + HNO3 + HCl)' },
    { value: 'HF_HClO4', label: 'HF + HClO4' },
  ];

  // Batch management functions
  const groupSamplesByBatch = (samples: Sample[]) => {
    const batches: Record<string, Sample[]> = {};
    const individualSamples: Sample[] = [];
    
    samples.forEach(sample => {
      if (isBatchSample(sample)) {
        const batchNumber = getBatchNumber(sample);
        if (batchNumber) {
          if (!batches[batchNumber]) {
            batches[batchNumber] = [];
          }
          batches[batchNumber].push(sample);
        }
      } else {
        individualSamples.push(sample);
      }
    });
    
    return { batches, individualSamples };
  };

  const isBatchSample = (sample: Sample): boolean => {
    return !!(sample.metadonnees?.batch_info?.batch_number);
  };

  const getBatchNumber = (sample: Sample): string | null => {
    return sample.metadonnees?.batch_info?.batch_number || null;
  };

  const getBatchPosition = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.position_in_batch || 1;
  };

  const getTotalSamplesInBatch = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.total_samples_in_batch || 1;
  };

  const toggleBatchExpansion = (batchNumber: string) => {
    const newExpanded = new Set(expandedBatches);
    if (newExpanded.has(batchNumber)) {
      newExpanded.delete(batchNumber);
    } else {
      newExpanded.add(batchNumber);
    }
    setExpandedBatches(newExpanded);
  };

  const handleSavePreparation = async () => {
    if (!selectedSample || !sampleId || initialWeight <= 0 || !digestionMethod) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const preparationData: ICPPreparationData = {
        sample_id: sampleId,
        initial_weight: initialWeight,
        digestion_method: digestionMethod,
        digestion_volume: digestionVolume,
        dilution_factor: dilutionFactor,
        preparation_notes: preparationNotes,
        timestamp: new Date().toISOString(),
        technician_id: user?.id || '',
        status: 'TERMINE',
      };

      const updatedSample = {
        ...selectedSample,
        statut: SampleStatus.LABORATOIRE_ICP_ANALYSE,
        metadonnees: {
          ...selectedSample.metadonnees,
          icp_preparation_data: preparationData,
          icp_preparation_completed: true,
        }
      };

      await samplesService.updateSample(selectedSample.id, updatedSample);
      
      setPreparationDialog(false);
      resetForm();
      loadICPPreparationData();
      
    } catch (error) {
      console.error('Error saving ICP preparation:', error);
      setError('Erreur lors de l\'enregistrement de la préparation');
    }
  };

  const resetForm = () => {
    setSelectedSample(null);
    setSampleId('');
    setInitialWeight(0);
    setDigestionMethod('');
    setDigestionVolume(100);
    setDilutionFactor(10);
    setPreparationNotes('');
  };

  // Add missing batch handling functions
  const initializeBatchSampleData = (samples: Sample[]) => {
    const initialData: Record<string, any> = {};
    
    samples.forEach(sample => {
      // 🔬 FIXED: Use default preparation weight (0.5g), NOT reception weight
      initialData[sample.id] = {
        initialWeight: 0.5, // Default preparation weight for ICP analysis
        digestionMethod: batchCommonValues.digestionMethod,
        digestionVolume: batchCommonValues.digestionVolume,
        dilutionFactor: batchCommonValues.dilutionFactor,
        preparationNotes: '',
      };
    });
    
    setBatchSampleData(initialData);
  };

  const applyCommonValuesToBatch = () => {
    if (!selectedBatch) return;
    
    const updatedData = { ...batchSampleData };
    selectedBatch.samples.forEach(sample => {
      if (updatedData[sample.id]) {
        if (batchCommonValues.applyToAll.digestionMethod) {
          updatedData[sample.id].digestionMethod = batchCommonValues.digestionMethod;
        }
        if (batchCommonValues.applyToAll.digestionVolume) {
          updatedData[sample.id].digestionVolume = batchCommonValues.digestionVolume;
        }
        if (batchCommonValues.applyToAll.dilutionFactor) {
          updatedData[sample.id].dilutionFactor = batchCommonValues.dilutionFactor;
        }
      }
    });
    setBatchSampleData(updatedData);
  };

  const updateBatchSampleData = (sampleId: string, field: string, value: any) => {
    setBatchSampleData(prev => ({
      ...prev,
      [sampleId]: {
        ...prev[sampleId],
        [field]: value
      }
    }));
  };

  // 🚀 PERFORMANCE: Optimized change handler for native inputs
  const handleNativeInputChange = useCallback((sampleId: string, field: string, value: number) => {
    updateBatchSampleData(sampleId, field, value);
  }, []);

  // Save batch preparation and move all samples to ICP analysis
  const handleSaveBatchPreparation = async () => {
    if (!selectedBatch?.samples.length) return;

    try {
      setBatchValidationLoading(true);
      
      // Validate all samples have required data
      const invalidSamples = selectedBatch.samples.filter(sample => {
        const sampleData = batchSampleData[sample.id];
        return !sampleData?.initialWeight || !sampleData?.digestionMethod || !sampleData?.digestionVolume;
      });

      if (invalidSamples.length > 0) {
        alert(`Veuillez compléter les données pour tous les échantillons. ${invalidSamples.length} échantillon(s) incomplet(s).`);
        return;
      }

      // 🚀 BATCH OPTIMIZATION: Build sample-specific updates for batch API
      const sampleIds = selectedBatch.samples.map(sample => sample.id);
      const batchUpdates: Record<string, any> = {};
      const sampleStatusMap: Record<string, SampleStatus> = {};
      
      // Build sample-specific metadata updates with per-sample status decisions
      selectedBatch.samples.forEach(sample => {
        const sampleData = batchSampleData[sample.id];
        
        const preparationData = {
          sampleId: sample.sample_id,
          initialWeight: sampleData.initialWeight,
          digestionMethod: sampleData.digestionMethod,
          digestionVolume: sampleData.digestionVolume,
          dilutionFactor: sampleData.dilutionFactor,
          preparationNotes: sampleData.preparationNotes || '',
          technician: user?.email || 'system',
          preparationDate: new Date().toISOString(),
          laboratoryType: 'ICP'
        };

        // For multi-lab samples (BGM or custom routing), check if both preparations will be complete
        const isMultiLabSample = sample.metadonnees?.multi_lab_workflow === true || 
                                 sample.metadonnees?.bgm_method_selected === true;
        
        // 🔥 CRITICAL FIX: Don't overwrite status if sample is already in validation/analysis workflow
        // This prevents ICP prep from removing samples from AU Supervisor's queue
        let newStatus: SampleStatus;
        if (isMultiLabSample) {
          const currentStatus = sample.statut;
          const protectedStatuses = [
            'VALIDATION_SERVICE',           // Already in validation - DON'T CHANGE
            'LABORATOIRE_AU_ANALYSE',       // In AU analysis - DON'T CHANGE
            'GENERATION_CERTIFICAT',        // Already generating certificate
          ];
          
          if (protectedStatuses.includes(currentStatus)) {
            newStatus = currentStatus as SampleStatus;
            console.log(`🔒 ICP Prep: Keeping status ${currentStatus} for multi-lab sample ${sample.sample_id} (AU workflow in progress)`);
          } else {
            newStatus = SampleStatus.LABORATOIRE_ICP_ANALYSE;
          }
        } else {
          newStatus = SampleStatus.LABORATOIRE_ICP_ANALYSE;
        }
        
        sampleStatusMap[sample.id] = newStatus;
        
        console.log(`🔍 ICP Prep - Sample ${sample.sample_id}:`, {
          isMultiLabSample,
          currentStatus: sample.statut,
          newStatus,
          auPrepComplete: sample.metadonnees?.au_preparation_completed,
          analyses_demandees: sample.metadonnees?.analyses_demandees
        });
        
        // Build sample-specific metadata (preserves existing metadata automatically)
        batchUpdates[sample.id] = {
          icp_preparation: preparationData,
          icp_preparation_completed: true,
          preparation_completed_at: new Date().toISOString(),
        };
        
        console.log(`📊 Sample ${sample.sample_id} preparation data:`, preparationData);
      });
      
      // Generate status comment for batch
      const firstSample = selectedBatch.samples[0];
      const firstSampleData = batchSampleData[firstSample.id];
      const isMultiLabBatch = selectedBatch.samples.some(s => 
        s.metadonnees?.multi_lab_workflow === true || s.metadonnees?.bgm_method_selected === true
      );
      const statusComment = isMultiLabBatch 
        ? `Préparation ICP terminée (multi-lab) - Lot: ${selectedBatch.batchNumber}, Méthode: ${firstSampleData.digestionMethod}`
        : `Préparation ICP terminée - Lot: ${selectedBatch.batchNumber}, Méthode: ${firstSampleData.digestionMethod}`;

      // 🚀 Check if all samples have the same target status
      const uniqueStatuses = [...new Set(Object.values(sampleStatusMap))];
      const allSampleIds = selectedBatch.samples.map(s => s.id);
      
      console.log(`🔄 Batch routing: ${allSampleIds.length} samples, unique statuses: ${uniqueStatuses.join(', ')}`);
      
      let totalUpdated = 0;
      let totalErrors: string[] = [];
      
      if (uniqueStatuses.length === 1) {
        // All samples have the same target status - use single batch update
        const result = await samplesService.updateSampleBatch(
          allSampleIds,
          batchUpdates,
          { status: uniqueStatuses[0], comment: statusComment }
        );
        totalUpdated += result.updated;
        totalErrors = [...totalErrors, ...result.errors];
      } else {
        // Mixed statuses - update metadata first, then status per-sample
        const metadataResult = await samplesService.updateSampleBatch(
          allSampleIds,
          batchUpdates,
          undefined  // No status update in first pass
        );
        totalUpdated += metadataResult.updated;
        totalErrors = [...totalErrors, ...metadataResult.errors];
        
        // Update statuses individually for samples that need it
        for (const sample of selectedBatch.samples) {
          const targetStatus = sampleStatusMap[sample.id];
          if (targetStatus !== sample.statut) {
            try {
              await samplesService.updateSampleStatus(sample.id, targetStatus, statusComment);
            } catch (error: any) {
              totalErrors.push(`Status update for ${sample.sample_id}: ${error.message}`);
            }
          }
        }
      }
      
      console.log(`✅ Batch update completed: ${totalUpdated} samples updated, ${totalErrors.length} errors`);
      if (totalErrors.length > 0) {
        console.warn('⚠️ Some samples had errors:', totalErrors);
        alert(`⚠️ Préparation terminée avec quelques erreurs:\n${totalErrors.join('\n')}`);
      }
      
      setBatchPreparationDialog(false);
      setBatchSampleData({});
      setSelectedBatch(null);
      loadICPPreparationData();
      
      alert(`✅ Préparation chimique ICP terminée pour ${selectedBatch.samples.length} échantillons du lot ${selectedBatch.batchNumber}`);
      
    } catch (error) {
      console.error('Error saving batch preparation:', error);
      alert('Erreur lors de la sauvegarde de la préparation du lot');
    } finally {
      setBatchValidationLoading(false);
    }
  };

  // Add missing functions for dilution calculation and value duplication
  const calculateDilutionFactor = (volumeTaken: number, finalVolume: number): number => {
    if (volumeTaken === 0 || finalVolume === 0) return 1;
    return finalVolume / volumeTaken;
  };

  const duplicateValueToAll = (field: string, value: any, sourceSampleId: string) => {
    if (!selectedBatch?.samples) return;
    
    const updatedData = { ...batchSampleData };
    
    selectedBatch.samples.forEach(sample => {
      if (sample.id !== sourceSampleId) {
        if (!updatedData[sample.id]) {
          updatedData[sample.id] = {};
        }
        updatedData[sample.id][field] = value;
        
        // Recalculate dilution factor if volumes changed
        if (field === 'volumeTaken' || field === 'finalVolume') {
          const sampleData = updatedData[sample.id];
          const volumeTaken = field === 'volumeTaken' ? value : (sampleData.volumeTaken || 0);
          const finalVolume = field === 'finalVolume' ? value : (sampleData.finalVolume || 0);
          updatedData[sample.id].calculatedDilutionFactor = calculateDilutionFactor(volumeTaken, finalVolume);
        }
      }
    });
    
    setBatchSampleData(updatedData);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Chargement des données ICP...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Science sx={{ color: '#2196f3' }} />
        Préparation Chimique ICP
        <Button
          startIcon={<Refresh />}
          onClick={loadICPPreparationData}
          variant="outlined"
          sx={{ ml: 'auto' }}
        >
          Actualiser
        </Button>
              </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
      <Card sx={{ 
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.primary.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.primary.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
                  <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>
                    En Attente
                  </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.pending_samples}
              </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  📋
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.warning.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.warning.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.warning.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>
                    En Cours
                  </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.in_progress}
              </Typography>
            </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.warning.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  ⏳
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.error.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.error.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.error.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>
                    Urgents
              </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.urgent_samples}
              </Typography>
            </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.error.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  🚨
            </Box>
          </Box>
        </CardContent>
      </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.success.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.success.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.success.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                  <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>
                    Terminé Aujourd'hui
                    </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.completed_today}
                    </Typography>
                  </Box>
                  <Box sx={{
                    width: 60,
                    height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.success.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                  }}>
                  ✅
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Samples Queue */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              {/* Header with title and search */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6">
                Échantillons en Attente de Préparation ICP
                  {totalSamples > 0 && (
                    <Chip 
                      label={`${totalSamples} total • ${batchCount} lots`} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ ml: 2 }}
                    />
                  )}
          </Typography>
          
                {/* 🔍 SEARCH BAR */}
                <TextField
                  size="small"
                  placeholder="Rechercher par ID ou N° Lot..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ 
                    width: { xs: '100%', sm: 300 },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearSearch}>
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
          
          {samples.length === 0 && !loading ? (
            <Box textAlign="center" py={4}>
                  <Science sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Aucun échantillon en attente
              </Typography>
              <Typography variant="body2" color="text.secondary">
                    Aucun échantillon n'est actuellement assigné pour la préparation chimique ICP.
              </Typography>
            </Box>
          ) : (
                <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID Échantillon / Lot</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Priorité</TableCell>
                        <TableCell>Méthodes</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        // Group samples by batch
                        const { batches, individualSamples } = groupSamplesByBatch(samples);
                        const items: Array<{type: 'batch', batchNumber: string, samples: Sample[]} | {type: 'individual', sample: Sample}> = [];
                        
                        // Add batch items
                        Object.entries(batches).forEach(([batchNumber, batchSamples]) => {
                          items.push({ type: 'batch', batchNumber, samples: batchSamples });
                        });
                        
                        // Add individual items
                        individualSamples.forEach(sample => {
                          items.push({ type: 'individual', sample });
                        });
                        
                        return items.map((item, index) => {
                          if (item.type === 'batch') {
                            const firstSample = item.samples[0];
                            const totalWeight = item.samples.reduce((sum, s) => sum + (s.metadonnees?.poids_kg || 0), 0);
                            const isExpanded = expandedBatches.has(item.batchNumber);
                            
                            // 🔧 CRITICAL FIX: Detect rejected batches for visual differentiation
                            const isRejectedBatch = item.samples.some(s => 
                              s.metadonnees?.validation_data?.action === 'reject' ||
                              s.metadonnees?.requires_repreparation === true
                            );
                            
                            // Define colors based on rejection status
                            const batchColors = isRejectedBatch ? {
                              backgroundColor: '#ffebee', // Light red
                              hoverColor: '#ffcdd2',       // Red hover
                              borderColor: '#f44336',      // Red border
                              labelColor: '#d32f2f'        // Dark red text
                            } : {
                              backgroundColor: '#e3f2fd', // Light blue (current)
                              hoverColor: '#bbdefb',       // Blue hover (current)
                              borderColor: '#2196f3',      // Blue border (current)
                              labelColor: '#1976d2'        // Blue text (current)
                            };
                            
                            return (
                              <React.Fragment key={`batch-${item.batchNumber}`}>
                                {/* Batch Header Row - Clickable */}
                                <TableRow 
                    sx={{
                                    backgroundColor: batchColors.backgroundColor, 
                                    '&:hover': { backgroundColor: batchColors.hoverColor },
                                    borderLeft: `4px solid ${batchColors.borderColor}`,
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => toggleBatchExpansion(item.batchNumber)}
                                >
                                  <TableCell colSpan={5}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      <IconButton size="small" sx={{ p: 0 }}>
                                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                      </IconButton>
                                      <Box sx={{ 
                                        backgroundColor: batchColors.borderColor, 
                                        color: 'white', 
                                        px: 2, 
                                        py: 0.5, 
                                        borderRadius: 1,
                                        fontWeight: 'bold'
                                      }}>
                                        {isRejectedBatch ? 'LOT REJETÉ' : 'LOT ICP'}
                                      </Box>
                                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: batchColors.labelColor }}>
                                        {item.batchNumber}
                          </Typography>
                          {isRejectedBatch && (
                            <Chip
                              label="⚠️ REPREP REQUISE"
                              size="small"
                              color="error"
                              variant="filled"
                              sx={{ 
                                fontWeight: 'bold',
                                '@keyframes pulse': {
                                  '0%': { opacity: 1 },
                                  '50%': { opacity: 0.7 },
                                  '100%': { opacity: 1 }
                                },
                                animation: 'pulse 2s infinite'
                              }}
                            />
                          )}
                          <Chip
                                        label={`${item.samples.length} échantillons`} 
                            size="small"
                                        color={isRejectedBatch ? "error" : "primary"} 
                                        variant="outlined"
                          />
                          <Chip
                                        label={`${totalWeight.toFixed(2)} kg total`} 
                            size="small"
                                        color="info" 
                                        variant="outlined"
                          />
                          <Typography variant="body2" color="text.secondary">
                                        Client: {firstSample.client?.company_name || 'N/A'}
                          </Typography>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        color={isRejectedBatch ? "error" : "primary"}
                                        startIcon={isRejectedBatch ? <Warning /> : <Science />}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedBatch({
                                            batchNumber: item.batchNumber,
                                            samples: item.samples
                                          });
                                          initializeBatchSampleData(item.samples);
                                          setBatchPreparationDialog(true);
                                        }}
                                        sx={{ ml: 'auto' }}
                                      >
                                        {isRejectedBatch ? 'Re-Préparer le Lot' : 'Préparer le Lot'}
                                      </Button>
                        </Box>
                                  </TableCell>
                                </TableRow>
                                
                                {/* Individual Sample Rows - Only show when expanded */}
                                {isExpanded && item.samples.map((sample) => (
                                  <TableRow 
                                    key={sample.id}
                    sx={{
                                      backgroundColor: '#f1f8ff',
                                      borderLeft: '2px solid #2196f3'
                                    }}
                                  >
                                    <TableCell sx={{ pl: 4 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" fontWeight="bold">
                            {sample.sample_id}
                          </Typography>
                          <Chip
                                          label={`Sac ${getBatchPosition(sample)}/${getTotalSamplesInBatch(sample)}`}
                            size="small"
                                          color="primary"
                            variant="outlined"
                                          sx={{ fontSize: '0.7rem' }}
                          />
                        </Box>
                                    </TableCell>
                                    <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                                    <TableCell>
                                      <Chip
                                        label={sample.priorite}
                                        size="small"
                                        color={sample.priorite === Priority.URGENTE ? 'error' : 'default'}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {sample.metadonnees?.analyses_demandees?.filter((a: string) => a.includes('ICP')).join(', ') || 'ICP Standard'}
                                    </TableCell>
                                    <TableCell>
                      <Button
                                        size="small"
                        variant="outlined"
                                        startIcon={<Science />}
                                        onClick={() => {
                                          setSelectedSample(sample);
                                          setSampleId(sample.sample_id);
                                          setPreparationDialog(true);
                                        }}
                                      >
                                        Préparer Individuel
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </React.Fragment>
                            );
                          } else {
                            // Individual sample
                            const sample = item.sample;
                            return (
                              <TableRow key={sample.id}>
                                <TableCell>
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    {sample.sample_id}
                          </Typography>
                                </TableCell>
                                <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={sample.priorite}
                        size="small"
                                    color={sample.priorite === Priority.URGENTE ? 'error' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>
                                  {sample.metadonnees?.analyses_demandees?.filter((a: string) => a.includes('ICP')).join(', ') || 'ICP Standard'}
                                </TableCell>
                                <TableCell>
                      <Button
                                    size="small"
                        variant="outlined"
                        startIcon={<Science />}
                                    onClick={() => {
                                      setSelectedSample(sample);
                                      setSampleId(sample.sample_id);
                                      setPreparationDialog(true);
                                    }}
                                  >
                                    Préparer
                      </Button>
                                </TableCell>
                              </TableRow>
                            );
                          }
                        });
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* 📄 PAGINATION CONTROLS */}
                <TablePagination
                  component="div"
                  count={totalSamples}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[50, 100, 250, 500]}
                  labelRowsPerPage="Échantillons par page:"
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} sur ${count} échantillons (${batchCount} lots sur cette page)`
                  }
                  sx={{
                    borderTop: '1px solid #e0e0e0',
                    '& .MuiTablePagination-toolbar': {
                      backgroundColor: '#f5f5f5',
                    }
                  }}
                />
                </>
          )}
        </CardContent>
      </Card>
        </Grid>

        {/* Reagent Alerts */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Inventory />
                Alertes Stock Réactifs ICP
              </Typography>
              
              <List>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="Aucune alerte de stock"
                    secondary="Le système de gestion des réactifs ICP sera bientôt disponible"
                  />
                  </ListItem>
            </List>
        </CardContent>
      </Card>
        </Grid>
      </Grid>

      {/* Individual Preparation Dialog */}
      <Dialog open={preparationDialog} onClose={() => setPreparationDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Science color="primary" />
            <Typography variant="h6">
              Formulaire de Préparation Chimique ICP
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Sample ID */}
            <TextField
              fullWidth
              label="ID Échantillon *"
              value={sampleId}
              onChange={(e) => setSampleId(e.target.value)}
              margin="normal"
              required
            />

            {/* Initial Weight - The main focus! */}
            <TextField
              fullWidth
              label="Poids Initial (g) *"
              type="number"
              value={initialWeight}
              onChange={(e) => setInitialWeight(Number(e.target.value))}
              margin="normal"
              required
              inputProps={{ min: 0, step: 0.001 }}
              helperText="Entrez le poids exact de l'échantillon en grammes"
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: '#f8f9fa',
                  fontSize: '1.1rem',
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 'bold',
                },
              }}
            />

            {/* Digestion Method */}
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Méthode de Digestion</InputLabel>
              <Select
                value={digestionMethod}
                onChange={(e) => setDigestionMethod(e.target.value)}
                label="Méthode de Digestion"
              >
                {digestionMethods.map((method) => (
                  <MenuItem key={method.value} value={method.value}>
                    {method.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Digestion Volume */}
            <TextField
              fullWidth
              label="Volume de Digestion (mL)"
              type="number"
              value={digestionVolume}
              onChange={(e) => setDigestionVolume(Number(e.target.value))}
              margin="normal"
              inputProps={{ min: 0 }}
            />

            {/* Dilution Factor */}
            <TextField
              fullWidth
              label="Facteur de Dilution"
              type="number"
              value={dilutionFactor}
              onChange={(e) => setDilutionFactor(Number(e.target.value))}
              margin="normal"
              inputProps={{ min: 1 }}
            />

            {/* Preparation Notes */}
            <TextField
              fullWidth
              label="Notes de Préparation"
              multiline
              rows={3}
              value={preparationNotes}
              onChange={(e) => setPreparationNotes(e.target.value)}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreparationDialog(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleSavePreparation}
            variant="contained"
            startIcon={<Save />}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Preparation Dialog */}
      <Dialog open={batchPreparationDialog} onClose={() => setBatchPreparationDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Science color="primary" />
            <Typography variant="h6">
              Préparation Chimique ICP - Lot {selectedBatch?.batchNumber}
                </Typography>
            <Chip 
              label={`${selectedBatch?.samples.length || 0} échantillons`} 
              color="primary" 
              variant="outlined" 
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Common Values Section */}
            <Card sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Settings />
                  Valeurs Communes (Appliquer à Tout le Lot)
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Méthode de Digestion</InputLabel>
                      <Select
                        value={batchCommonValues.digestionMethod}
                        onChange={(e) => setBatchCommonValues({
                          ...batchCommonValues,
                          digestionMethod: e.target.value
                        })}
                        label="Méthode de Digestion"
                      >
                        {digestionMethods.map((method) => (
                          <MenuItem key={method.value} value={method.value}>
                            {method.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Volume de Digestion (mL)"
                      type="number"
                      value={batchCommonValues.digestionVolume}
                      onChange={(e) => setBatchCommonValues({
                        ...batchCommonValues,
                        digestionVolume: Number(e.target.value)
                      })}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Facteur de Dilution"
                      type="number"
                      value={batchCommonValues.dilutionFactor}
                      onChange={(e) => setBatchCommonValues({
                        ...batchCommonValues,
                        dilutionFactor: Number(e.target.value)
                      })}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                </Grid>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={applyCommonValuesToBatch}
                  startIcon={<AutoAwesome />}
                  fullWidth
                >
                  Appliquer les Valeurs Communes à Tous les Échantillons
                </Button>
              </CardContent>
            </Card>

            {/* Individual Samples Table */}
            <Typography variant="h6" gutterBottom>
              Paramètres Individuels par Échantillon
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID Échantillon</TableCell>
                    <TableCell>Poids Initial (g)</TableCell>
                    <TableCell>Méthode Digestion</TableCell>
                    <TableCell>Volume (mL)</TableCell>
                    <TableCell>Facteur Dilution</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedBatch?.samples.map((sample) => (
                    <TableRow key={sample.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {sample.sample_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <NativeNumberInput
                          value={batchSampleData[sample.id]?.initialWeight || 0}
                          onChange={handleNativeInputChange}
                          sampleId={sample.id}
                          field="initialWeight"
                          hasValue={(batchSampleData[sample.id]?.initialWeight || 0) > 0}
                          placeholder="Poids (g)"
                          min={0}
                          step={0.001}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={batchSampleData[sample.id]?.digestionMethod || ''}
                          onChange={(e) => updateBatchSampleData(sample.id, 'digestionMethod', e.target.value)}
                          sx={{ width: 150 }}
                        >
                          {digestionMethods.map((method) => (
                            <MenuItem key={method.value} value={method.value}>
                              {method.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <NativeNumberInput
                          value={batchSampleData[sample.id]?.digestionVolume || 100}
                          onChange={handleNativeInputChange}
                          sampleId={sample.id}
                          field="digestionVolume"
                          hasValue={(batchSampleData[sample.id]?.digestionVolume || 0) > 0}
                          placeholder="Volume (mL)"
                          min={0}
                          step={1}
                        />
                      </TableCell>
                      <TableCell>
                        <NativeNumberInput
                          value={batchSampleData[sample.id]?.dilutionFactor || 10}
                          onChange={handleNativeInputChange}
                          sampleId={sample.id}
                          field="dilutionFactor"
                          hasValue={(batchSampleData[sample.id]?.dilutionFactor || 0) > 0}
                          placeholder="Facteur"
                          min={1}
                          step={1}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          multiline
                          rows={1}
                          value={batchSampleData[sample.id]?.preparationNotes || ''}
                          onChange={(e) => updateBatchSampleData(sample.id, 'preparationNotes', e.target.value)}
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchPreparationDialog(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleSaveBatchPreparation}
            variant="contained"
            color="primary"
            startIcon={<Save />}
            disabled={loading}
          >
            {loading ? 'Enregistrement...' : 'Valider le Lot ICP'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Preparation Dialog - Full Screen SAA Style */}
      <Dialog 
        open={batchPreparationDialog} 
        onClose={() => setBatchPreparationDialog(false)} 
        maxWidth={false}
        fullScreen
        PaperProps={{ 
          sx: { 
            backgroundColor: '#f8f9fa',
            '& .MuiDialogTitle-root': {
              backgroundColor: '#1976d2',
              color: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 1000
            }
          } 
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Science sx={{ fontSize: '1.8rem' }} />
              <Typography variant="h5" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                🧪 Préparation Chimique ICP - Lot {selectedBatch?.batchNumber}
              </Typography>
              <Chip 
                label={`${selectedBatch?.samples.length || 0} échantillons`} 
                color="success" 
                variant="outlined" 
                sx={{ 
                  color: 'white', 
                  borderColor: 'white',
                  fontSize: '0.75rem'
                }}
              />
            </Box>
            
            {/* Progress Indicator */}
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
                Prêts: {selectedBatch?.samples?.filter(sample => {
                  const sampleData = batchSampleData[sample.id];
                  return sampleData?.initialWeight && sampleData?.digestionMethod && sampleData?.initialVolume;
                }).length || 0}/{selectedBatch?.samples?.length || 0}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
          <Box sx={{ mt: 1 }}>
            {/* Batch Samples Table - Full SAA Style */}
            <Card>
              <CardContent sx={{ p: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                    📋 Saisie des Préparations - Lot {selectedBatch?.batchNumber}
                  </Typography>
                  
                  {/* Methods Display */}
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {['HCl-HNO3', 'Eau Régale', 'HNO3'].map((method) => (
                      <Chip
                        key={method}
                        label={method}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: '20px' }}
                      />
                    ))}
                  </Box>
                </Box>
                
                <TableContainer sx={{ 
                  maxHeight: 'calc(100vh - 300px)', 
                  overflowY: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1
                }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ 
                        backgroundColor: '#e8f5e8',
                        '& .MuiTableCell-head': {
                          backgroundColor: '#1976d2',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          padding: '6px 8px',
                          whiteSpace: 'nowrap'
                        }
                      }}>
                        <TableCell>Échantillon</TableCell>
                        <TableCell>Poids (g)</TableCell>
                        <TableCell>Méthode</TableCell>
                        <TableCell>Vol Initial (mL)</TableCell>
                        <TableCell>Vol Prélevé (mL)</TableCell>
                        <TableCell>Vol Final (mL)</TableCell>
                        <TableCell>Facteur</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedBatch?.samples
                        .sort((a, b) => {
                          const aNum = parseInt(a.sample_id.replace(/\D/g, ''));
                          const bNum = parseInt(b.sample_id.replace(/\D/g, ''));
                          if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
                          return a.sample_id.localeCompare(b.sample_id);
                        })
                        .map((sample, index) => {
                        const sampleData = batchSampleData[sample.id] || {};
                        
                        return (
                          <TableRow 
                            key={sample.id}
                            sx={{ 
                              backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white',
                              '&:hover': { backgroundColor: '#f0f8ff' },
                              '& .MuiTableCell-root': {
                                padding: '4px 6px',
                                fontSize: '0.75rem'
                              }
                            }}
                          >
                            {/* Sample ID */}
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                                {sample.sample_id}
                              </Typography>
                            </TableCell>
                            
                            {/* Weight with Copy Button */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={sampleData.initialWeight || 0}
                                  onChange={(e) => updateBatchSampleData(sample.id, 'initialWeight', Number(e.target.value))}
                                  inputProps={{ min: 0, step: 0.01 }}
                                  sx={{ width: 60 }}
                                  InputProps={{
                                    sx: { fontSize: '0.7rem', height: '28px' }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('initialWeight', sampleData.initialWeight, sample.id)}
                                  title="Copier vers tous"
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                            
                            {/* Method with Copy Button */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <FormControl size="small" sx={{ minWidth: 80 }}>
                                  <Select
                                    value={sampleData.digestionMethod || ''}
                                    onChange={(e) => updateBatchSampleData(sample.id, 'digestionMethod', e.target.value)}
                                    displayEmpty
                                    sx={{ 
                                      fontSize: '0.7rem', 
                                      height: '28px',
                                      '& .MuiSelect-select': { py: 0.5 }
                                    }}
                                    renderValue={(selected) => {
                                      if (!selected) return <em style={{fontSize: '0.65rem', color: '#999'}}>Méthode</em>;
                                      return selected;
                                    }}
                                  >
                                    <MenuItem value="HCl-HNO3">
                                      <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>HCl-HNO3</Typography>
                                    </MenuItem>
                                    <MenuItem value="Eau Régale">
                                      <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>Eau Régale</Typography>
                                    </MenuItem>
                                    <MenuItem value="HNO3">
                                      <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>HNO3</Typography>
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('digestionMethod', sampleData.digestionMethod, sample.id)}
                                  title="Copier vers tous"
                                  disabled={!sampleData.digestionMethod}
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                            
                            {/* Initial Volume with Copy Button */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={sampleData.initialVolume || 0}
                                  onChange={(e) => updateBatchSampleData(sample.id, 'initialVolume', Number(e.target.value))}
                                  inputProps={{ min: 0, step: 0.1 }}
                                  sx={{ width: 60 }}
                                  InputProps={{
                                    sx: { fontSize: '0.7rem', height: '28px' }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('initialVolume', sampleData.initialVolume, sample.id)}
                                  title="Copier vers tous"
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                            
                            {/* Volume Prélevé with Copy Button */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={sampleData.volumeTaken || 0}
                                  onChange={(e) => updateBatchSampleData(sample.id, 'volumeTaken', Number(e.target.value))}
                                  inputProps={{ min: 0, step: 0.1 }}
                                  sx={{ width: 60 }}
                                  placeholder="0.0"
                                  InputProps={{
                                    sx: { fontSize: '0.7rem', height: '28px' }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('volumeTaken', sampleData.volumeTaken, sample.id)}
                                  title="Copier vers tous"
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                            
                            {/* Volume Final with Copy Button */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={sampleData.finalVolume || 0}
                                  onChange={(e) => updateBatchSampleData(sample.id, 'finalVolume', Number(e.target.value))}
                                  inputProps={{ min: 0, step: 0.1 }}
                                  sx={{ width: 60 }}
                                  placeholder="0.0"
                                  InputProps={{
                                    sx: { fontSize: '0.7rem', height: '28px' }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('finalVolume', sampleData.finalVolume, sample.id)}
                                  title="Copier vers tous"
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                            
                            {/* Calculated Dilution Factor */}
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                color={sampleData.calculatedDilutionFactor > 1 ? 'primary' : 'text.secondary'}
                                fontWeight={sampleData.calculatedDilutionFactor > 1 ? 'bold' : 'normal'}
                                sx={{ fontSize: '0.7rem' }}
                              >
                                {sampleData.calculatedDilutionFactor ? sampleData.calculatedDilutionFactor.toFixed(2) : '1.00'}x
                              </Typography>
                            </TableCell>
                            
                            {/* Status */}
                            <TableCell>
                              <Chip
                                label={sampleData.initialWeight && sampleData.digestionMethod && sampleData.initialVolume ? 'OK' : 'Manque'}
                                size="small"
                                color={sampleData.initialWeight && sampleData.digestionMethod && sampleData.initialVolume ? 'success' : 'warning'}
                                sx={{ 
                                  fontSize: '0.6rem', 
                                  height: '18px',
                                  '& .MuiChip-label': { px: 1 }
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Summary Footer */}
                <Box sx={{ 
                  mt: 2, 
                  p: 1.5, 
                  backgroundColor: '#e3f2fd', 
                  borderRadius: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2
                }}>
                  <Typography variant="subtitle2" sx={{ color: '#1976d2', fontWeight: 'bold', fontSize: '0.85rem' }}>
                    📊 Résumé: {selectedBatch?.samples.length || 0} échantillons
                  </Typography>
                  
                  <Box display="flex" gap={3} alignItems="center" flexWrap="wrap">
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Poids Total</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                        {selectedBatch?.samples.reduce((sum, sample) => {
                          const sampleData = batchSampleData[sample.id];
                          return sum + (sampleData?.initialWeight || 0);
                        }, 0).toFixed(2)} g
                      </Typography>
                    </Box>
                    
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Prêts</Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main" sx={{ fontSize: '0.8rem' }}>
                        {selectedBatch?.samples.filter(sample => {
                          const sampleData = batchSampleData[sample.id];
                          return sampleData?.initialWeight && sampleData?.digestionMethod && sampleData?.initialVolume;
                        }).length || 0}
                      </Typography>
                    </Box>
                    
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Volume Total</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                        {selectedBatch?.samples.reduce((sum, sample) => {
                          const sampleData = batchSampleData[sample.id];
                          return sum + (sampleData?.initialVolume || 0);
                        }, 0).toFixed(1)} mL
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 2, 
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e0e0e0',
          position: 'sticky',
          bottom: 0,
          zIndex: 1000
        }}>
          <Button 
            onClick={() => setBatchPreparationDialog(false)}
            sx={{ fontSize: '0.8rem' }}
          >
            Annuler
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveBatchPreparation}
            disabled={batchValidationLoading}
            startIcon={batchValidationLoading ? <CircularProgress size={16} /> : <Science />}
            sx={{ fontSize: '0.8rem' }}
          >
            {batchValidationLoading ? 'Sauvegarde en cours...' : `Valider ${selectedBatch?.samples.length || 0} échantillons`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default ICPPreparationChimiqueDashboard; 
